/**
 * Requirements data: the calls behind links, criteria, bulk edits, saved views, keys, AI jobs,
 * proposals, suggested defects and sequences. Live mode calls the hub and re-reads its state;
 * demo mode (?mock=1) runs the same rules in the browser, and AI jobs there are simulated so
 * every screen can be tried without a machine.
 */
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AI_DEFAULT_SETTINGS, newPrefixedId, proposalSummary, splitSections } from '@orgus/protocol';
import type {
  AiJob,
  AiJobsSettings,
  AiJobsState,
  AiTask,
  BacklogItem,
  DefectSignal,
  Id,
  ItemBulkDeleteResult,
  ItemBulkPatch,
  ItemHistoryEntry,
  ItemKind,
  ItemLink,
  ItemPatch,
  LinkInput,
  MissionSequence,
  MissionsState,
  Proposal,
  ProposalPatch,
  ProposedItem,
  SavedView,
  SavedViewInput,
  SequenceInput,
} from '@orgus/protocol';
import { ApiError, api, apiGet } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { refreshMissions } from '../missions/data.ts';
import { MISSIONS_EXT, missionsSlice } from '../missions/model.ts';
import { MockRefusal, mockCheckCriterion, mockCreateItem, mockDeleteItem, mockDeleteView, mockLink, mockSaveView, mockSetPrefix, mockUnlink, mockUpdateItem } from '../missions/mock.ts';
import { AIJOBS_EXT, aiJobsSlice } from './model.ts';
import { mockAiJobs, mockProposalFor } from './mock.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const currentMissions = (): MissionsState => missionsSlice(useOrgus.getState().ext) ?? { items: [], missions: [] };
const putMissions = (data: MissionsState) => useOrgus.getState().applyExt({ ext: MISSIONS_EXT, type: 'state', data });
const currentJobs = (): AiJobsState => aiJobsSlice(useOrgus.getState().ext) ?? { jobs: [], settings: AI_DEFAULT_SETTINGS };
const putJobs = (data: AiJobsState) => useOrgus.getState().applyExt({ ext: AIJOBS_EXT, type: 'state', data });

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function refusal(e: unknown, what: string): never {
  if (e instanceof MockRefusal) throw new ApiError({ what, why: e.message, next: 'Fix it and try again.' });
  throw e;
}

async function live<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  const out = await api<T>(path, { method, body: body ?? (method === 'DELETE' ? undefined : {}) });
  await refreshMissions().catch(() => undefined);
  return out;
}

// ---------------------------------------------------------------------------
// AI jobs slice
// ---------------------------------------------------------------------------

export type JobsPhase = 'loading' | 'ready' | 'unavailable';

/** state.ext.aijobs. Demo mode seeds demo jobs on first use. */
export function useAiJobs(): { state: AiJobsState | null; phase: JobsPhase } {
  const { slice, phase, mode } = useOrgus(useShallow((s) => ({ slice: aiJobsSlice(s.ext), phase: s.phase, mode: s.mode })));
  useEffect(() => {
    if (mode === 'mock' && phase === 'ready' && !slice) putJobs(mockAiJobs(Date.now()));
  }, [mode, phase, slice]);
  if (phase !== 'ready') return { state: null, phase: 'loading' };
  if (!slice) return { state: null, phase: mode === 'mock' ? 'loading' : 'unavailable' };
  return { state: slice, phase: 'ready' };
}

export function useJob(id: Id | null | undefined): AiJob | undefined {
  const { state } = useAiJobs();
  return useMemo(() => (id ? state?.jobs.find((j) => j.id === id) : undefined), [state, id]);
}

export async function refreshJobs(): Promise<void> {
  if (isMock()) return;
  putJobs(await apiGet<AiJobsState>('/api/aijobs'));
}

export async function cancelJob(id: Id): Promise<void> {
  if (!isMock()) {
    await api(`/api/aijobs/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} });
    await refreshJobs().catch(() => undefined);
    return;
  }
  const s = currentJobs();
  putJobs({ ...s, jobs: s.jobs.map((j) => (j.id === id && (j.state === 'running' || j.state === 'queued') ? { ...j, state: 'cancelled', stage: 'finished', endedAt: Date.now() } : j)) });
}

export async function clearJob(id: Id): Promise<void> {
  if (!isMock()) {
    await api(`/api/aijobs/${encodeURIComponent(id)}`, { method: 'DELETE' });
    await refreshJobs().catch(() => undefined);
    return;
  }
  const s = currentJobs();
  putJobs({ ...s, jobs: s.jobs.filter((j) => j.id !== id) });
}

export async function saveAiSettings(patch: Partial<AiJobsSettings>): Promise<AiJobsSettings> {
  if (!isMock()) {
    const out = await api<AiJobsSettings>('/api/aijobs/settings', { method: 'PATCH', body: patch });
    await refreshJobs().catch(() => undefined);
    return out;
  }
  const s = currentJobs();
  const settings = { ...s.settings, ...patch };
  putJobs({ ...s, settings });
  return settings;
}

// ---------------------------------------------------------------------------
// Items: bulk, criteria, history
// ---------------------------------------------------------------------------

export async function bulkUpdate(input: ItemBulkPatch): Promise<{ updated: Id[]; skipped: { id: Id; reason: string }[] }> {
  if (!isMock()) return live('/api/missions/items/bulk', 'POST', input);
  await delay(200);
  let s = currentMissions();
  const updated: Id[] = [];
  const skipped: { id: Id; reason: string }[] = [];
  const { addLabels, removeLabels, ...rest } = input.patch;
  for (const id of input.ids) {
    const it = s.items.find((i) => i.id === id);
    if (!it) continue;
    const patch: ItemPatch = { ...rest };
    if (addLabels || removeLabels) patch.labels = [...new Set([...it.labels.filter((l) => !(removeLabels ?? []).includes(l)), ...(addLabels ?? []).map((l) => l.toLowerCase())])];
    try {
      s = mockUpdateItem(s, id, patch, Date.now()).state;
      updated.push(id);
    } catch (e) {
      skipped.push({ id, reason: e instanceof Error ? e.message : String(e) });
    }
  }
  putMissions(s);
  return { updated, skipped };
}

/** Delete many items: children before their parents; each one that may not go is skipped with the reason. */
export async function bulkDelete(ids: readonly Id[]): Promise<ItemBulkDeleteResult> {
  if (!isMock()) return live('/api/missions/items/bulk-delete', 'POST', { ids });
  await delay(200);
  let s = currentMissions();
  const depth = (id: Id): number => {
    let d = 0;
    for (let cur = s.items.find((i) => i.id === id); cur?.parentId && d < 50; cur = s.items.find((i) => i.id === cur!.parentId)) d++;
    return d;
  };
  const deleted = new Set<Id>();
  const skipped: { id: Id; reason: string }[] = [];
  for (const id of [...new Set(ids)].sort((a, b) => depth(b) - depth(a))) {
    if (!s.items.some((i) => i.id === id)) {
      skipped.push({ id, reason: 'It no longer exists.' });
      continue;
    }
    try {
      s = mockDeleteItem(s, id);
      deleted.add(id);
    } catch (e) {
      skipped.push({ id, reason: e instanceof Error ? e.message : String(e) });
    }
  }
  putMissions(s);
  return { deleted: ids.filter((id) => deleted.has(id)), skipped };
}

export async function checkCriterion(itemId: Id, criterionId: string, done: boolean): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/items/${encodeURIComponent(itemId)}/criteria/${encodeURIComponent(criterionId)}`, 'POST', { done }));
  putMissions(mockCheckCriterion(currentMissions(), itemId, criterionId, done, Date.now()));
}

/** ORGU-155: put back one change Autopilot's grooming made. */
export async function undoGroom(groomId: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/autopilot/groomed/${encodeURIComponent(groomId)}/undo`, 'POST'));
}

export async function itemHistory(id: Id): Promise<ItemHistoryEntry[]> {
  if (!isMock()) return (await apiGet<{ entries: ItemHistoryEntry[] }>(`/api/missions/items/${encodeURIComponent(id)}/history`)).entries;
  await delay(150);
  const it = currentMissions().items.find((i) => i.id === id);
  if (!it) return [];
  const out: ItemHistoryEntry[] = [{ at: it.createdAt, by: it.aiDrafted ? 'srikanth' : 'owner', action: 'item.create', ...(it.source ? { note: `From ${it.source.name}${it.source.section ? `, ${it.source.section}` : ''}` } : {}) }];
  if (it.status !== 'backlog' && it.status !== 'triage') out.push({ at: it.updatedAt - 60_000, by: it.statusNote?.kind === 'mission' ? 'orgus' : 'owner', action: it.statusNote?.kind === 'mission' ? 'item.sync' : 'item.update', field: 'status', from: 'ready', to: it.status, ...(it.statusNote ? { note: it.statusNote.text } : {}) });
  for (const c of it.acceptance.filter((x) => x.done)) out.push({ at: it.updatedAt - 30_000, by: c.checkedBy ?? 'owner', action: 'ac.check', field: c.id, from: false, to: true, note: c.text });
  return out;
}

// ---------------------------------------------------------------------------
// Links, views, keys
// ---------------------------------------------------------------------------

export async function addLink(input: LinkInput): Promise<ItemLink> {
  if (!isMock()) return live<ItemLink>('/api/missions/links', 'POST', input);
  try {
    const r = mockLink(currentMissions(), input, Date.now());
    putMissions(r.state);
    return r.link;
  } catch (e) {
    return refusal(e, 'The items were not linked.');
  }
}

export async function removeLink(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/links/${encodeURIComponent(id)}`, 'DELETE'));
  putMissions(mockUnlink(currentMissions(), id));
}

export async function saveView(input: SavedViewInput): Promise<SavedView> {
  if (!isMock()) return live<SavedView>('/api/missions/views', 'POST', input);
  try {
    const r = mockSaveView(currentMissions(), input, Date.now());
    putMissions(r.state);
    return r.view;
  } catch (e) {
    return refusal(e, 'The view was not saved.');
  }
}

export async function deleteView(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/views/${encodeURIComponent(id)}`, 'DELETE'));
  putMissions(mockDeleteView(currentMissions(), id));
}

export async function setKeyPrefix(projectId: Id, prefix: string): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/keys/${encodeURIComponent(projectId)}`, 'PATCH', { prefix }));
  putMissions(mockSetPrefix(currentMissions(), projectId, prefix.toUpperCase()));
}

// ---------------------------------------------------------------------------
// AI: import, write, detect
// ---------------------------------------------------------------------------

export interface ImportInput {
  projectId: Id;
  attachmentId?: Id;
  text?: string;
  name?: string;
  guidance?: string;
  parentId?: Id | null;
  machineId?: Id | null;
  model?: string | null;
}

export interface TaskInput {
  task: Exclude<AiTask, 'import' | 'defect'>;
  projectId?: Id;
  itemId?: Id;
  request?: string;
  kind?: ItemKind | null;
  parentId?: Id | null;
  attachmentIds?: Id[];
  machineId?: Id | null;
  model?: string | null;
}

const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')) as T;

/** Run a simulated job in demo mode: it moves through the stages, then leaves a proposal. */
function simulate(task: AiTask, title: string, projectId: Id | null, itemId: Id | null, make: () => Proposal | null): AiJob {
  const now = Date.now();
  const s = currentJobs();
  const job: AiJob = {
    id: newPrefixedId('job', now),
    task,
    title,
    projectId,
    itemId,
    machineId: 'm-studio',
    model: s.settings.model,
    state: 'running',
    stage: 'sending',
    by: 'owner',
    createdAt: now,
    startedAt: now,
    endedAt: null,
    cost: null,
    tokens: null,
    turns: null,
    events: 0,
    outputChars: 0,
    expectedChars: 3000,
    error: null,
    proposalId: null,
    summary: null,
  };
  putJobs({ ...s, jobs: [job, ...s.jobs] });
  const patch = (p: Partial<AiJob>) => {
    const cur = currentJobs();
    putJobs({ ...cur, jobs: cur.jobs.map((j) => (j.id === job.id ? { ...j, ...p } : j)) });
  };
  const alive = () => currentJobs().jobs.find((j) => j.id === job.id)?.state === 'running';
  const steps: [number, Partial<AiJob>][] = [
    [500, { stage: 'starting' }],
    [1100, { stage: 'reading', events: 3 }],
    [1800, { stage: 'writing', events: 9, outputChars: 900 }],
    [2600, { stage: 'writing', events: 17, outputChars: 2100 }],
    [3300, { stage: 'checking', outputChars: 3000 }],
  ];
  for (const [ms, p] of steps) setTimeout(() => alive() && patch(p), ms);
  setTimeout(() => {
    if (!alive()) return;
    const proposal = make();
    if (proposal) {
      const ms = currentMissions();
      putMissions({ ...ms, proposals: [proposal, ...(ms.proposals ?? [])] });
    }
    patch({ state: 'done', stage: 'finished', endedAt: Date.now(), cost: 0.0312 + Math.random() * 0.05, tokens: { input: 5400, output: 1200 }, turns: 1, proposalId: proposal?.id ?? null, summary: proposal ? proposalSummary(proposal.items) : null });
  }, 3800);
  return job;
}

export async function startImport(input: ImportInput): Promise<{ job: AiJob; sections: number; chars: number; warnings: string[] }> {
  if (!isMock()) {
    const out = await api<{ job: AiJob; sections: number; chars: number; warnings: string[] }>('/api/missions/import', { method: 'POST', body: clean(input), timeoutMs: 60_000 });
    await refreshJobs().catch(() => undefined);
    return out;
  }
  await delay(300);
  const text = input.text ?? '# Payments\nCustomers pay by card.\n\n## Refunds\nSupport refunds within 30 days.';
  const sections = splitSections(text);
  const name = input.name ?? 'document';
  const job = simulate('import', `Import ${name}`, input.projectId, input.parentId ?? null, () => mockProposalFor('import', input.projectId, name, sections, null, currentMissions()));
  return { job, sections: sections.length, chars: text.length, warnings: [] };
}

export async function startAiTask(input: TaskInput): Promise<{ job: AiJob }> {
  if (!isMock()) {
    const out = await api<{ job: AiJob }>('/api/missions/ai', { method: 'POST', body: clean(input), timeoutMs: 60_000 });
    await refreshJobs().catch(() => undefined);
    return out;
  }
  await delay(250);
  // Demo mode: ?draftfail=1 shows how a refused draft looks.
  if (new URLSearchParams(location.search).has('draftfail')) throw new ApiError({ what: 'The draft was not started.', why: 'No machine with a model is online.', next: 'Try again when a machine is back.' });
  const s = currentMissions();
  const item = input.itemId ? s.items.find((i) => i.id === input.itemId) ?? null : null;
  const projectId = item?.projectId ?? input.projectId ?? null;
  const title = item ? `${input.task === 'criteria' ? 'Acceptance criteria for' : input.task === 'split' ? 'Split' : input.task === 'estimate' ? 'Estimate' : input.task === 'improve' ? 'Improve' : 'Draft'} ${item.key}` : `Draft: ${input.request ?? 'from attachments'}`;
  const job = simulate(input.task, title, projectId, item?.id ?? null, () => (projectId ? mockProposalFor(input.task, projectId, input.request ?? '', [], item, s, input.kind ?? null) : null));
  return { job };
}

// ---------------------------------------------------------------------------
// Proposals
// ---------------------------------------------------------------------------

export async function fetchProposal(id: Id): Promise<Proposal> {
  const inState = currentMissions().proposals?.find((p) => p.id === id);
  if (isMock() || inState?.status === 'open') {
    if (!inState) throw new ApiError({ what: 'That proposal is not here.', why: 'It may have been applied or discarded.', next: 'Open the review queue.' });
    return inState;
  }
  return apiGet<Proposal>(`/api/missions/proposals/${encodeURIComponent(id)}`);
}

function putProposal(p: Proposal) {
  const s = currentMissions();
  putMissions({ ...s, proposals: (s.proposals ?? []).map((x) => (x.id === p.id ? p : x)) });
}

export async function patchProposal(id: Id, patch: ProposalPatch): Promise<Proposal> {
  if (!isMock()) return live<Proposal>(`/api/missions/proposals/${encodeURIComponent(id)}`, 'PATCH', patch);
  const p = await fetchProposal(id);
  const next = { ...p, ...patch, updatedAt: Date.now() };
  putProposal(next);
  return next;
}

export async function discardProposal(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/proposals/${encodeURIComponent(id)}/discard`, 'POST'));
  const p = await fetchProposal(id);
  putProposal({ ...p, status: 'discarded', updatedAt: Date.now() });
}

export interface ApplyResult {
  created: BacklogItem[];
  updated: Id[];
  linked: Id[];
  skipped: string[];
}

export async function applyProposal(id: Id, patch: ProposalPatch): Promise<ApplyResult> {
  if (!isMock()) return live<ApplyResult>(`/api/missions/proposals/${encodeURIComponent(id)}/apply`, 'POST', patch);
  await delay(400);
  const p = { ...(await fetchProposal(id)), ...patch };
  let s = currentMissions();
  const created: BacklogItem[] = [];
  const idOf = new Map<string, Id>();
  const accepted = p.items.filter((x) => x.decision === 'accepted');
  if (!accepted.length && !p.edits.some((e) => e.decision === 'accepted') && !p.links.some((l) => l.decision === 'accepted')) {
    throw new ApiError({ what: 'Nothing was applied.', why: 'Accept at least one item, change or link first.', next: 'Accept what you want, then apply.' });
  }
  const byRef = new Map(p.items.map((x) => [x.ref, x]));
  const parentFor = (x: ProposedItem): Id | null => {
    for (let r = x.parentRef; r; r = byRef.get(r)?.parentRef ?? '') {
      const made = idOf.get(r);
      if (made) return made;
    }
    return x.parentId ?? null;
  };
  for (const x of accepted) {
    const r = mockCreateItem(s, { projectId: p.projectId, kind: x.kind, title: x.title, body: x.body, priority: x.priority, estimate: x.estimate, labels: x.labels, acceptance: x.acceptance, ...(x.defect ? { defect: x.defect } : {}), parentId: parentFor(x), aiDrafted: true, source: p.source ? { kind: 'document', name: p.source.name, sections: x.sections, proposalId: p.id } : { kind: 'ai', name: p.title, proposalId: p.id } }, Date.now());
    s = r.state;
    idOf.set(x.ref, r.item.id);
    created.push(r.item);
  }
  const updated: Id[] = [];
  for (const e of p.edits.filter((x) => x.decision === 'accepted')) {
    s = mockUpdateItem(s, e.itemId, e.patch as ItemPatch, Date.now()).state;
    updated.push(e.itemId);
  }
  const linked: Id[] = [];
  for (const l of p.links.filter((x) => x.decision === 'accepted')) {
    const r = mockLink(s, { type: l.type, from: l.from, to: l.to }, Date.now());
    s = r.state;
    linked.push(r.link.id);
  }
  if (p.kind === 'split' && p.targetItemId && p.dropOriginal) s = mockUpdateItem(s, p.targetItemId, { status: 'dropped' }, Date.now()).state;
  if (p.kind === 'defect' && p.signalId) s = { ...s, signals: (s.signals ?? []).map((x) => (x.id === p.signalId ? { ...x, status: 'accepted' as const, itemId: created[0]?.id } : x)) };
  s = { ...s, proposals: (s.proposals ?? []).map((x) => (x.id === id ? { ...p, status: 'applied' as const, createdIds: created.map((c) => c.id), appliedAt: Date.now(), appliedBy: 'owner' } : x)) };
  putMissions(s);
  return { created, updated, linked, skipped: [] };
}

// ---------------------------------------------------------------------------
// Suggested defects
// ---------------------------------------------------------------------------

export async function acceptSignal(id: Id, mode: 'direct' | 'ai', opts: { machineId?: Id | null; model?: string | null } = {}): Promise<{ item?: BacklogItem; job?: AiJob }> {
  if (!isMock()) {
    const out = await live<{ item?: BacklogItem; job?: AiJob }>(`/api/missions/signals/${encodeURIComponent(id)}/accept`, 'POST', clean({ mode, ...opts }));
    if (out.job) await refreshJobs().catch(() => undefined);
    return out;
  }
  const s = currentMissions();
  const sig = (s.signals ?? []).find((x) => x.id === id);
  if (!sig) throw new ApiError({ what: 'The defect was not created.', why: 'That suggestion is gone.', next: 'Reload the review queue.' });
  if (mode === 'ai') {
    const job = simulate('defect', `Defect from: ${sig.title}`, sig.projectId, null, () => mockProposalFor('defect', sig.projectId, sig.title, [], null, currentMissions(), 'defect', sig));
    return { job };
  }
  try {
    const r = mockCreateItem(s, { projectId: sig.projectId, kind: 'defect', title: sig.title, body: `Suggested by Orgus from a failure. The evidence is attached.\n\n\`\`\`\n${sig.evidence}\n\`\`\``, defect: { severity: 'major', actual: sig.evidence.split('\n').pop() ?? '' } }, Date.now());
    putMissions({ ...r.state, signals: (r.state.signals ?? []).map((x) => (x.id === id ? { ...x, status: 'accepted' as const, itemId: r.item.id, decidedBy: 'owner', decidedAt: Date.now() } : x)) });
    return { item: r.item };
  } catch (e) {
    return refusal(e, 'The defect was not created.');
  }
}

export async function dismissSignal(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/signals/${encodeURIComponent(id)}/dismiss`, 'POST'));
  const s = currentMissions();
  putMissions({ ...s, signals: (s.signals ?? []).map((x): DefectSignal => (x.id === id ? { ...x, status: 'dismissed', decidedBy: 'owner', decidedAt: Date.now() } : x)) });
}

export async function scanSignals(): Promise<number> {
  if (!isMock()) return (await live<{ added: number }>('/api/missions/signals/scan', 'POST')).added;
  await delay(300);
  return 0;
}

// ---------------------------------------------------------------------------
// Sequences
// ---------------------------------------------------------------------------

export async function startSequence(input: SequenceInput): Promise<MissionSequence> {
  if (!isMock()) return live<MissionSequence>('/api/missions/sequences', 'POST', input);
  await delay(300);
  const s = currentMissions();
  const kids = s.items.filter((i) => i.parentId === input.itemId && i.status !== 'done' && i.status !== 'dropped');
  if (!kids.length) throw new ApiError({ what: 'The sequence was not started.', why: 'It has no open story, task or defect for a mission to work.', next: 'Add stories first.' });
  const seq: MissionSequence = { id: newPrefixedId('seq', Date.now()), itemId: input.itemId, projectId: kids[0]!.projectId, itemIds: kids.map((k) => k.id), missionIds: kids.map(() => null), index: 0, state: 'running', start: input.start, by: 'owner', createdAt: Date.now(), updatedAt: Date.now() };
  putMissions({ ...s, sequences: [seq, ...(s.sequences ?? [])] });
  return seq;
}

export async function stopSequence(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/missions/sequences/${encodeURIComponent(id)}/stop`, 'POST'));
  const s = currentMissions();
  putMissions({ ...s, sequences: (s.sequences ?? []).map((x) => (x.id === id ? { ...x, state: 'stopped' as const, reason: 'Stopped by owner.' } : x)) });
}

